import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send, Loader2 } from "lucide-react";
import { CONTACT, IMAGES } from "../lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  company: "",
  email: "",
  phone: "",
  project_type: "Commercial",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm(initialState);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Something went wrong sending your message. Please try again.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        const first = detail[0];
        const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : "field";
        msg = `${String(field).replace(/_/g, " ")}: ${first?.msg || "invalid value"}`;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#F5F5F0]">
      {/* HEADER */}
      <section className="relative overflow-hidden -mt-32 md:-mt-36">
        <img
          src={IMAGES.residentialWarm}
          alt="Architectural interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-44 md:pt-56 pb-32 md:pb-44">
          <span className="eyebrow text-[#EB1D25]">Get in Touch</span>
          <h1 className="mt-10 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight max-w-[22ch] leading-[1.1]">
            Let's work together.
          </h1>
          <p className="mt-10 text-white/80 text-base md:text-lg leading-relaxed max-w-xl">
            Tell us a little about your project — we'll come back to you with the right team and next steps.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-24 md:py-32 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* DETAILS */}
          <aside className="lg:col-span-4 space-y-10" data-testid="contact-details">
            <div>
              <span className="eyebrow">Email</span>
              <a
                href={`mailto:${CONTACT.emailPrimary}`}
                className="mt-4 block font-heading text-xl md:text-2xl text-[#111111] hover:text-[#EB1D25] transition-colors break-all"
                data-testid="contact-email"
              >
                <Mail size={18} strokeWidth={1.5} className="inline-block mr-2 text-[#EB1D25]" />
                {CONTACT.emailPrimary}
              </a>
            </div>

            <div>
              <span className="eyebrow">Phone</span>
              <ul className="mt-4 space-y-3">
                <li>
                  <a
                    href={`tel:+64${CONTACT.phoneMartin.replace(/\s/g, "").replace(/^0/, "")}`}
                    className="font-heading text-lg md:text-xl text-[#111111] hover:text-[#EB1D25] transition-colors flex items-center gap-2"
                    data-testid="contact-phone-martin"
                  >
                    <Phone size={18} strokeWidth={1.5} className="text-[#EB1D25]" />
                    Martin (Director) · {CONTACT.phoneMartin}
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:+64${CONTACT.phoneMiguel.replace(/\s/g, "").replace(/^0/, "")}`}
                    className="font-heading text-lg md:text-xl text-[#111111] hover:text-[#EB1D25] transition-colors flex items-center gap-2"
                    data-testid="contact-phone-miguel"
                  >
                    <Phone size={18} strokeWidth={1.5} className="text-[#EB1D25]" />
                    Miguel (Project Coordinator) · {CONTACT.phoneMiguel}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <span className="eyebrow">Locations</span>
              <ul className="mt-4 space-y-5">
                <li className="flex gap-3">
                  <MapPin size={18} strokeWidth={1.5} className="mt-1 text-[#EB1D25]" />
                  <div>
                    <p className="font-heading text-[#111111] font-semibold">Auckland</p>
                    <p className="text-[#545454]">{CONTACT.aucklandAddress}</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <MapPin size={18} strokeWidth={1.5} className="mt-1 text-[#EB1D25]" />
                  <div>
                    <p className="font-heading text-[#111111] font-semibold">Christchurch</p>
                    <p className="text-[#545454]">{CONTACT.christchurchAddress}</p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <MapPin size={18} strokeWidth={1.5} className="mt-1 text-[#EB1D25]" />
                  <div>
                    <p className="font-heading text-[#111111] font-semibold">Queenstown</p>
                    <p className="text-[#545454]">{CONTACT.queenstownAddress}</p>
                  </div>
                </li>
              </ul>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-8">
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#E2E2DF] p-8 md:p-12"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                <Field label="Name" required>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-input-name"
                    className="w-full bg-transparent border-b border-[#A0A0A0] py-3 text-[#111111] focus:border-[#EB1D25] focus:outline-none transition-colors"
                  />
                </Field>
                <Field label="Company">
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    data-testid="contact-input-company"
                    className="w-full bg-transparent border-b border-[#A0A0A0] py-3 text-[#111111] focus:border-[#EB1D25] focus:outline-none transition-colors"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-input-email"
                    className="w-full bg-transparent border-b border-[#A0A0A0] py-3 text-[#111111] focus:border-[#EB1D25] focus:outline-none transition-colors"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="contact-input-phone"
                    className="w-full bg-transparent border-b border-[#A0A0A0] py-3 text-[#111111] focus:border-[#EB1D25] focus:outline-none transition-colors"
                  />
                </Field>
                <Field label="Project Type" required>
                  <select
                    name="project_type"
                    value={form.project_type}
                    onChange={handleChange}
                    data-testid="contact-select-project-type"
                    className="w-full bg-transparent border-b border-[#A0A0A0] py-3 text-[#111111] focus:border-[#EB1D25] focus:outline-none transition-colors appearance-none cursor-pointer"
                  >
                    <option value="Commercial">Commercial</option>
                    <option value="Residential">Residential</option>
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Message" required>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      required
                      data-testid="contact-input-message"
                      className="w-full bg-transparent border-b border-[#A0A0A0] py-3 text-[#111111] focus:border-[#EB1D25] focus:outline-none transition-colors resize-none"
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-12 inline-flex items-center gap-3 bg-[#EB1D25] text-white font-heading uppercase tracking-widest text-xs font-semibold px-8 py-5 hover:bg-[#111111] transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Message <Send size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="py-0" data-testid="contact-map-section">
        <div className="grid grid-cols-1 md:grid-cols-3">
          <div className="aspect-[4/3] md:aspect-auto md:h-[480px] border-t border-b border-[#E2E2DF]">
            <iframe
              title="Auckland Location"
              src="https://www.google.com/maps?q=28+Constellation+Drive,+Auckland&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-auckland"
            />
          </div>
          <div className="aspect-[4/3] md:aspect-auto md:h-[480px] border-t border-b md:border-l border-[#E2E2DF]">
            <iframe
              title="Christchurch Location"
              src="https://www.google.com/maps?q=Christchurch,+New+Zealand&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-christchurch"
            />
          </div>
          <div className="aspect-[4/3] md:aspect-auto md:h-[480px] border-t border-b md:border-l border-[#E2E2DF]">
            <iframe
              title="Queenstown Location"
              src="https://www.google.com/maps?q=Queenstown,+New+Zealand&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.6) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="map-queenstown"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#545454] font-heading font-semibold block mb-2">
        {label}
        {required && <span className="text-[#EB1D25] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
